// src/elements/hudReel/unmuteNudge.ts
// WHY: Tasteful pulse/glow on mute button for users who previously opted-in but hit muted autoplay.
export type UnmuteNudgeOptions = {
  pulseCount: number;
  pulseDurationMs: number;
  pulseGapMs: number;
  ttlMs: number;
  cooldownDays: number;
  glowColor: string;
  ringColor: string;
  selectorMuteButton: string[];
  selectorAudioGate: string[];
  storageKeys: string[];
  debounceMs: number;
};

const DEFAULTS: UnmuteNudgeOptions = {
  pulseCount: 3,
  pulseDurationMs: 600,
  pulseGapMs: 600,
  ttlMs: 3600,
  cooldownDays: 7,
  glowColor: "rgba(255,184,0,0.45)", // amber
  ringColor: "rgba(255,184,0,0.40)",
  selectorMuteButton: [
    '[data-role="mute"]', '[data-hud-mute]', '.hud-mute',
    'button[aria-label*="mute" i]', 'button[aria-label*="sound" i]',
    '.mute-button'
  ],
  selectorAudioGate: ['.reel-audio-gate', '.hud-audio-gate', '[data-audio-gate]'],
  storageKeys: ['storycore:audioAllowed','sc:audioAllowed','audioAllowed','sc:reelGateSeen'],
  debounceMs: 700,
};

let opts: UnmuteNudgeOptions = { ...DEFAULTS };
let stylesInjected = false;
let active = false;
let cleanup: Array<() => void> = [];
let btnRef: HTMLElement | null = null;
let iconRef: HTMLElement | null = null;
let ringRef: HTMLElement | null = null;
let debounceTimer = 0;

export function initUnmuteNudge(userOpts?: Partial<UnmuteNudgeOptions>): void {
  // WHY: global idempotency when called per-reel
  if ((window as any).__scUnmuteNudgeInited) return;
  (window as any).__scUnmuteNudgeInited = true;

  opts = { ...DEFAULTS, ...(userOpts || {}) };
  injectStyles();

  add(document, 'sc:autoplay-muted', onMutedEvent);
  add(document, 'sc:autoplay-with-audio-failed', onMutedEvent);
  // tolerant alias if you emit something simpler elsewhere
  add(document, 'sc:reel:muted-autoplay', onMutedEvent);
}

export function cancelUnmuteNudge(): void { stop(); }

/* ---------------- internals ---------------- */

function onMutedEvent(ev: Event) {
  const d = (ev as CustomEvent<{ section?: HTMLElement; video?: HTMLVideoElement }>).detail || {};
  const section = resolveSection((d.section || (d.video as any)) ?? null);
  const video = d.video || section?.querySelector('video');
  if (!section || !video) return;

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => maybeShow(section, video), opts.debounceMs);
}

function maybeShow(section: HTMLElement, video: HTMLVideoElement) {
  if (active) return;
  if (!isUserAudioOptIn()) return; // WHY: only nudge return visitors who chose audio
  if (isAudioGateVisible(section)) return;
  if (!video.muted) return;
  if (sessionShown()) return;
  if (cooldownActive()) return;

  const btn = findMuteButton(section);
  if (!btn) return;

  start(btn, video, section);
}

function start(btn: HTMLElement, video: HTMLVideoElement, section: HTMLElement) {
  active = true;
  markSessionShown();
  setLastShownNow();

  btnRef = btn;
  iconRef = (btn.querySelector<HTMLElement>('svg, .icon, [data-icon]') || btn) as HTMLElement;
  iconRef.classList.add('sc-unmute-nudge__icon');
  btn.classList.add('sc-unmute-nudge--active');

  ringRef = document.createElement('span');
  ringRef.className = 'sc-unmute-nudge__ring';
  btn.appendChild(ringRef);

  cleanup.push(
    add(video, 'volumechange', () => !video.muted && stop()),
    add(document, 'pointerdown', stop, { once: true }),
    add(document, 'sc:hud:sleep', stop),
    add(document, 'sc:section:leave', (e) => {
      const leaving = (e as CustomEvent<{ section: HTMLElement }>).detail?.section;
      if (leaving && leaving === section) stop();
    })
  );

  const ttl = window.setTimeout(stop, opts.ttlMs);
  cleanup.push(() => clearTimeout(ttl));
}

function stop() {
  if (!active) return;
  active = false;

  try {
    btnRef?.classList.remove('sc-unmute-nudge--active');
    iconRef?.classList.remove('sc-unmute-nudge__icon');
    ringRef?.parentElement?.removeChild(ringRef);
  } catch {}

  btnRef = iconRef = ringRef = null;
  for (const off of cleanup.splice(0)) { try { off(); } catch {} }
}

/* ---------------- helpers ---------------- */

function isUserAudioOptIn(): boolean {
  // tolerate multiple keys/values; treat recent gate accept as opt-in
  for (const k of opts.storageKeys) {
    const v = getStorage(localStorage, k) ?? getStorage(sessionStorage, k);
    if (typeof v === 'string' && /^(1|true|yes)$/i.test(v)) return true;
  }
  const grace = (window as any).__storycoreGateGraceUntil || 0;
  return Date.now() < grace;
}
function getStorage(store: Storage, key: string): string | null { try { return store.getItem(key); } catch { return null; } }

function isAudioGateVisible(scope: HTMLElement): boolean {
  for (const sel of opts.selectorAudioGate) {
    const el = scope.querySelector<HTMLElement>(sel) || document.querySelector<HTMLElement>(sel);
    if (el && el.offsetParent) return true;
  }
  return false;
}

function findMuteButton(scope: HTMLElement): HTMLElement | null {
  for (const sel of opts.selectorMuteButton) {
    const el = scope.querySelector<HTMLElement>(sel) || document.querySelector<HTMLElement>(sel);
    if (el) return el;
  }
  return null;
}

function resolveSection(node: Element | null): HTMLElement | null {
  return node ? (node.closest(".sc-section,[data-section-index],[id^='section-']") as HTMLElement | null) : null;
}

function sessionShown(): boolean { try { return sessionStorage.getItem('sc:unmutenudge:sessionShown') === '1'; } catch { return false; } }
function markSessionShown() { try { sessionStorage.setItem('sc:unmutenudge:sessionShown', '1'); } catch {} }

function cooldownActive(): boolean {
  const last = getLastShownAt();
  if (!last) return false;
  return (Date.now() - last) / 86_400_000 < opts.cooldownDays;
}
function setLastShownNow() { try { localStorage.setItem('sc:unmutenudge:lastShownAt', String(Date.now())); } catch {} }
function getLastShownAt(): number | null {
  try { const raw = localStorage.getItem('sc:unmutenudge:lastShownAt'); return raw ? Number(raw) : null; }
  catch { return null; }
}

function add<T extends EventTarget>(t: T, type: string, fn: EventListenerOrEventListenerObject, opt?: AddEventListenerOptions | boolean) {
  t.addEventListener(type, fn as any, opt);
  return () => t.removeEventListener(type, fn as any, opt as any);
}

function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const css = `
:root{
  --unmute-pulse-scale: 1.12;
  --unmute-glow-color: ${opts.glowColor};
  --unmute-ring-color: ${opts.ringColor};
  --unmute-pulse-duration: ${opts.pulseDurationMs}ms;
  --unmute-pulse-gap: ${opts.pulseGapMs}ms;
}
.sc-unmute-nudge--active{
  position:relative;
  box-shadow:0 0 22px var(--unmute-glow-color);
}
.sc-unmute-nudge__icon{
  will-change:transform,opacity;
  display:inline-block;
  transform-origin:center;
  animation:
    sc-unmute-pulse var(--unmute-pulse-duration) ease-out 0ms 1,
    sc-unmute-pulse var(--unmute-pulse-duration) ease-out calc(var(--unmute-pulse-duration) + var(--unmute-pulse-gap)) 1,
    sc-unmute-pulse var(--unmute-pulse-duration) ease-out calc(2*(var(--unmute-pulse-duration) + var(--unmute-pulse-gap))) 1;
}
.sc-unmute-nudge__ring{
  position:absolute; inset:-6px; border-radius:9999px; pointer-events:none;
  will-change:transform,opacity;
  background:radial-gradient(closest-side,var(--unmute-ring-color), rgba(255,184,0,0) 70%);
  animation:
    sc-unmute-ring var(--unmute-pulse-duration) ease-out 0ms 1,
    sc-unmute-ring var(--unmute-pulse-duration) ease-out calc(var(--unmute-pulse-duration) + var(--unmute-pulse-gap)) 1,
    sc-unmute-ring var(--unmute-pulse-duration) ease-out calc(2*(var(--unmute-pulse-duration) + var(--unmute-pulse-gap))) 1;
}
@keyframes sc-unmute-pulse{0%{transform:scale(1)}45%{transform:scale(var(--unmute-pulse-scale))}100%{transform:scale(1)}}
@keyframes sc-unmute-ring{0%{opacity:0;transform:scale(.6)}10%{opacity:.85}100%{opacity:0;transform:scale(1.25)}}
@media (prefers-reduced-motion: reduce){
  .sc-unmute-nudge__icon{
    animation: sc-unmute-glow 2000ms ease-out 0ms 1 !important;
  }
  .sc-unmute-nudge__ring{ display:none !important; }
  @keyframes sc-unmute-glow{
    0%{ filter: drop-shadow(0 0 0 var(--unmute-glow-color)); }
    50%{ filter: drop-shadow(0 0 14px var(--unmute-glow-color)); }
    100%{ filter: drop-shadow(0 0 0 var(--unmute-glow-color)); }
  }
}
`;
  const styleEl = document.createElement('style');
  styleEl.id = 'sc-unmute-nudge-styles';
  styleEl.textContent = css;
  document.head.appendChild(styleEl);
}